/**
 * AdminSiteSettingsPanel — Iter 67aj.
 *
 * Admin form for the platform's social-media URLs and the legal copy
 * (Terms of service + Privacy policy). Saves to a singleton
 * `db.site_settings` doc via `PUT /api/site-settings`. The footer reads the
 * social URLs and conditionally renders the icons; the public /terms and
 * /privacy pages render the saved HTML.
 *
 * Design choice: we accept HTML (not Markdown) so admins can paste pre-styled
 * content from Word/Google Docs and it Just Works. A future iteration can
 * swap the Textarea for a TipTap editor; the API stays the same.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save, Instagram, Facebook, Linkedin, ScrollText, Lock, Cookie, Search, Building2, RotateCcw } from "lucide-react";

const SOCIAL_FIELDS = [
  { key: "social_instagram", label: "Instagram", placeholder: "https://instagram.com/yourhandle", icon: Instagram, color: "text-pink-600 bg-pink-50 ring-pink-100" },
  { key: "social_facebook",  label: "Facebook",  placeholder: "https://facebook.com/yourpage",   icon: Facebook,  color: "text-blue-600 bg-blue-50 ring-blue-100" },
  { key: "social_x",         label: "X (Twitter)", placeholder: "https://x.com/yourhandle",      icon: XInline,   color: "text-neutral-900 bg-neutral-50 ring-neutral-200" },
  { key: "social_tiktok",    label: "TikTok",    placeholder: "https://tiktok.com/@yourhandle", icon: TikTokInline, color: "text-fuchsia-600 bg-fuchsia-50 ring-fuchsia-100" },
  { key: "social_linkedin",  label: "LinkedIn",  placeholder: "https://linkedin.com/company/yours", icon: Linkedin, color: "text-sky-700 bg-sky-50 ring-sky-100" },
];

function _safeUrlOrEmpty(value) {
  // Allow empty (= "remove this social"), reject obvious garbage, normalise
  // missing https://. Returning null means "keep field empty".
  const v = (value || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

export default function AdminSiteSettingsPanel() {
  const [form, setForm] = useState({
    social_instagram: "", social_facebook: "", social_x: "",
    social_tiktok: "",    social_linkedin: "",
    terms_html: "",       privacy_html: "", about_html: "", cookie_html: "",
    gsc_verification_token: "",
  });
  const [meta, setMeta] = useState({ terms_updated_at: null, privacy_updated_at: null, about_updated_at: null });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/site-settings")
      .then((r) => {
        const d = r.data || {};
        setForm({
          social_instagram: d.social_instagram || "",
          social_facebook:  d.social_facebook  || "",
          social_x:         d.social_x         || "",
          social_tiktok:    d.social_tiktok    || "",
          social_linkedin:  d.social_linkedin  || "",
          terms_html:       d.terms_html       || "",
          privacy_html:     d.privacy_html     || "",
          about_html:       d.about_html       || "",
          cookie_html:      d.cookie_html      || "",
          gsc_verification_token: d.gsc_verification_token || "",
        });
        setMeta({
          terms_updated_at: d.terms_updated_at,
          privacy_updated_at: d.privacy_updated_at,
          about_updated_at: d.about_updated_at,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Iter 69 — "Reset to GDPR placeholder" for terms / privacy / about.
  // Calls the backend reset-seed endpoint which clears the saved HTML so
  // the next /site-settings GET re-runs `_ensure_legal_seeds` and returns
  // the canonical Albanian-jurisdiction copy. Confirms before destroying
  // admin-authored content.
  const resetToSeed = async (kind) => {
    const labelMap = { terms: "Terms of service", privacy: "Privacy policy", about: "About page" };
    if (!window.confirm(`Reset ${labelMap[kind]} to the default GDPR placeholder? Your current edits will be replaced.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/site-settings/reset-seed?kind=${kind}`);
      setForm((f) => ({ ...f, [`${kind}_html`]: data[`${kind}_html`] || "" }));
      setMeta({
        terms_updated_at: data.terms_updated_at,
        privacy_updated_at: data.privacy_updated_at,
        about_updated_at: data.about_updated_at,
      });
      toast.success(`${labelMap[kind]} reset to default placeholder.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normalise URLs before posting so the footer doesn't get a mix of
      // www.example.com and https://example.com.
      const payload = {
        social_instagram: _safeUrlOrEmpty(form.social_instagram),
        social_facebook:  _safeUrlOrEmpty(form.social_facebook),
        social_x:         _safeUrlOrEmpty(form.social_x),
        social_tiktok:    _safeUrlOrEmpty(form.social_tiktok),
        social_linkedin:  _safeUrlOrEmpty(form.social_linkedin),
        terms_html:       form.terms_html,
        privacy_html:     form.privacy_html,
        about_html:       form.about_html,
        cookie_html:      form.cookie_html,
        gsc_verification_token: form.gsc_verification_token,
      };
      const { data } = await api.put("/site-settings", payload);
      setMeta({
        terms_updated_at: data.terms_updated_at,
        privacy_updated_at: data.privacy_updated_at,
        about_updated_at: data.about_updated_at,
      });
      toast.success("Site settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="admin-site-settings-loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-neutral-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="admin-site-settings">
      {/* ----- Social media URLs ----- */}
      <section>
        <header className="mb-5">
          <h3 className="font-display text-2xl font-semibold tracking-tight">Social media handles</h3>
          <p className="text-sm text-neutral-500 mt-1">URLs you paste here power the icons in the site footer. Leave a field empty to hide that icon.</p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-site-settings-socials">
          {SOCIAL_FIELDS.map(({ key, label, placeholder, icon: Icon, color }) => (
            <div key={key}>
              <Label className="flex items-center gap-2 mb-1.5">
                <span className={`h-7 w-7 rounded-lg flex items-center justify-center ring-1 ${color}`}>
                  <Icon className="h-4 w-4" />
                </span>
                {label}
              </Label>
              <Input
                type="url"
                className="rounded-xl"
                placeholder={placeholder}
                value={form[key]}
                onChange={(e) => update(key, e.target.value)}
                data-testid={`admin-site-${key}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ----- Terms of service ----- */}
      <section>
        <header className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-amber-600" /> Terms of service
            </h3>
            <p className="text-sm text-neutral-500 mt-1">Shown publicly at <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">/terms</a>. Accepts HTML — paste from Word/Google Docs and it'll keep formatting.</p>
          </div>
          {meta.terms_updated_at && (
            <span className="text-xs text-neutral-500" data-testid="admin-terms-updated">
              Last updated {new Date(meta.terms_updated_at).toLocaleString()}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => resetToSeed("terms")}
            disabled={saving}
            className="h-7 text-[11px] text-neutral-500 hover:text-amber-700 hover:bg-amber-50 gap-1.5"
            data-testid="admin-terms-reset-seed"
          >
            <RotateCcw className="h-3 w-3" /> Reset to placeholder
          </Button>
        </header>
        <Textarea
          rows={12}
          className="font-mono text-xs rounded-xl"
          placeholder={`<h2>1. Acceptance</h2>\n<p>By using RentalCar.al you agree to…</p>`}
          value={form.terms_html}
          onChange={(e) => update("terms_html", e.target.value)}
          data-testid="admin-terms-html"
        />
      </section>

      {/* ----- Privacy policy ----- */}
      <section>
        <header className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Lock className="h-5 w-5 text-emerald-600" /> Privacy policy
            </h3>
            <p className="text-sm text-neutral-500 mt-1">Shown publicly at <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">/privacy</a>. Same HTML rules as Terms above.</p>
          </div>
          {meta.privacy_updated_at && (
            <span className="text-xs text-neutral-500" data-testid="admin-privacy-updated">
              Last updated {new Date(meta.privacy_updated_at).toLocaleString()}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => resetToSeed("privacy")}
            disabled={saving}
            className="h-7 text-[11px] text-neutral-500 hover:text-emerald-700 hover:bg-emerald-50 gap-1.5"
            data-testid="admin-privacy-reset-seed"
          >
            <RotateCcw className="h-3 w-3" /> Reset to placeholder
          </Button>
        </header>
        <Textarea
          rows={12}
          className="font-mono text-xs rounded-xl"
          placeholder={`<h2>What we collect</h2>\n<p>Names, emails, payment metadata…</p>`}
          value={form.privacy_html}
          onChange={(e) => update("privacy_html", e.target.value)}
          data-testid="admin-privacy-html"
        />
      </section>

      {/* ----- About RentalCar.al (Iter 69) ----- */}
      <section>
        <header className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" /> About page
            </h3>
            <p className="text-sm text-neutral-500 mt-1">Shown publicly at <a href="/about" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">/about</a>. Use it to introduce RentalCar.al, the team, and your mission. Same HTML rules as Terms.</p>
          </div>
          {meta.about_updated_at && (
            <span className="text-xs text-neutral-500" data-testid="admin-about-updated">
              Last updated {new Date(meta.about_updated_at).toLocaleString()}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => resetToSeed("about")}
            disabled={saving}
            className="h-7 text-[11px] text-neutral-500 hover:text-indigo-700 hover:bg-indigo-50 gap-1.5"
            data-testid="admin-about-reset-seed"
          >
            <RotateCcw className="h-3 w-3" /> Reset to placeholder
          </Button>
        </header>
        <Textarea
          rows={12}
          className="font-mono text-xs rounded-xl"
          placeholder={`<h2>About RentalCar.al</h2>\n<p>Albania's first vendor-neutral car-rental marketplace…</p>`}
          value={form.about_html}
          onChange={(e) => update("about_html", e.target.value)}
          data-testid="admin-about-html"
        />
      </section>

      {/* ----- Cookie consent banner ----- */}
      <section>
        <header className="mb-3">
          <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Cookie className="h-5 w-5 text-amber-700" /> Cookie banner
          </h3>
          <p className="text-sm text-neutral-500 mt-1">Shown to first-time visitors as a slide-up banner. Renters dismiss it via "Accept all" or "Essentials only". Leave empty to use the default copy. HTML allowed (links to <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">/privacy</a> are encouraged).</p>
        </header>
        <Textarea
          rows={4}
          className="font-mono text-xs rounded-xl"
          placeholder={`<p>We use cookies to keep you signed in… <a href="/privacy">Learn more</a>.</p>`}
          value={form.cookie_html}
          onChange={(e) => update("cookie_html", e.target.value)}
          data-testid="admin-cookie-html"
        />
      </section>

      {/* ----- Iter 67en++++ — Google Search Console verification token ----- */}
      <section>
        <header className="mb-3">
          <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Search className="h-5 w-5 text-amber-700" /> Google Search Console
          </h3>
          <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
            Paste the <strong>token</strong> from{" "}
            <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              Google Search Console
            </a>{" "}
            → "Add property" → "HTML tag" method. We inject the meta tag into every page so GSC can verify ownership.{" "}
            <strong>Paste only the content value</strong> (e.g.{" "}
            <code className="font-mono bg-neutral-100 px-1 rounded">abc123_def456…</code>), not the full{" "}
            <code className="font-mono bg-neutral-100 px-1 rounded">&lt;meta …&gt;</code> tag.
          </p>
        </header>
        <input
          type="text"
          className="w-full px-3 py-2 rounded-xl border border-neutral-200 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-amber-400"
          placeholder="e.g. abc1234567890_xyz-EXAMPLE"
          value={form.gsc_verification_token}
          onChange={(e) => update("gsc_verification_token", e.target.value)}
          data-testid="admin-gsc-token"
          maxLength={128}
        />
        {form.gsc_verification_token && (
          <p className="mt-2 text-[11px] text-emerald-700 font-mono">
            Will render as: <code className="bg-emerald-50 px-1 rounded">&lt;meta name="google-site-verification" content="{form.gsc_verification_token}" /&gt;</code>
          </p>
        )}
      </section>

      <div className="sticky bottom-4 z-10 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-gradient-to-br from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-200 px-8"
          data-testid="admin-site-settings-save"
        >
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

/* ───── Inline glyphs for social networks Lucide doesn't ship ───── */

function XInline(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.7 3h3.3l-7.2 8.2L22 21h-6.6l-5.2-6.7L4.3 21H1l7.7-8.8L1.4 3h6.7l4.7 6.1L17.7 3zm-1.2 16h1.8L7.6 4.9H5.7L16.5 19z" />
    </svg>
  );
}

function TikTokInline(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.6 8.3a6.7 6.7 0 0 1-3.9-1.2v7.7a5.4 5.4 0 1 1-5.4-5.4c.3 0 .6 0 .8.1v2.7a2.7 2.7 0 1 0 1.9 2.6V3h2.7a4 4 0 0 0 4 3.6v1.7z"/>
    </svg>
  );
}
